/**
 * GP-1395: the one list of drugs an order-detail view is allowed to render.
 *
 * Two different arrays describe "the drugs on this order" and they drift:
 *
 *  - `order_items` (`order.items[]`) — the AUTHORITY. One row per dispensed Rx;
 *    every split/merge/relink moves these rows.
 *  - `metadata.billingData.medications[]` — a SNAPSHOT taken at billing time and
 *    patched by later writes. Rich (strength, packSize, UPC) but lossy.
 *
 * The Order Tracking header reads `order.medicationName` (recomputed from the
 * items on every write), while Product Verification and the Prescription
 * Hardcopies tab strip read the snapshot — so when the snapshot loses an Rx the
 * header lists a drug that has no hardcopy tab and no verification row.
 * Observed on prod ORD-20260813-172419-553 (GSP London): Rx 1484226
 * Apo-Trazodone merged back from a TS sidecar, then a stale-cache PATCH from the
 * Product Verification dialog wholesale-replaced `metadata` with its pre-merge
 * copy and dropped the drug from the snapshot. The order shipped with a drug the
 * floor could neither hardcopy-check nor product-verify.
 *
 * The write-side hole is closed in OrdersService.update() (a metadata PATCH can
 * no longer shrink the medications array). This helper is the read-side
 * counterpart, and also the only thing that rescues orders already damaged: a
 * drug that exists as a line item is ALWAYS rendered, whether or not the billing
 * snapshot remembers it.
 *
 * Union, not replacement — the snapshot's richer fields (strength, packSize) are
 * kept where it has them, and its entries stay in their existing order so the
 * tab strip doesn't reshuffle for healthy orders.
 */

export interface DrugListMedication {
  rxNumber?: string | number;
  medicationName?: string;
  strength?: string;
  quantity?: number;
  daysSupply?: number;
  din?: string;
  upc?: string;
  drugId?: string;
  packSize?: number;
}

/** The OrderItem shape as it comes over the wire from GET /api/orders/:pharmacyId/:id. */
export interface DrugListOrderItem {
  rxNumber?: string | number;
  medicationName?: string;
  strength?: string;
  quantity?: number;
  daysSupply?: number;
  productDin?: string;
  productUpc?: string;
  drugId?: string;
  fulfillmentStage?: string | null;
}

export interface DrugListOrderLike {
  items?: DrugListOrderItem[];
  metadata?: {
    billingData?: {
      medications?: DrugListMedication[];
    };
  };
}

/**
 * The one way an Rx number is turned into a comparison key. Kroll Rx numbers
 * arrive as both numbers and strings, and stray whitespace shows up in
 * hand-edited metadata — matching two spellings of the same Rx as different
 * drugs would report a verified drug as "Not verified", so every consumer that
 * joins on Rx (this module and productVerificationBoxSummary) uses this.
 */
export const rxKey = (rxNumber: unknown): string =>
  rxNumber == null ? '' : String(rxNumber).trim();

export interface OrderDrugListEntry extends DrugListMedication {
  /** Normalized to a string so callers can compare against Rx keys directly. */
  rxNumber: string;
  medicationName: string;
  /**
   * The billing snapshot has no entry for this Rx — it was recovered from the
   * order's line items. Views may flag it; they must never drop it.
   */
  fromLineItemOnly: boolean;
  /** Per-drug fulfillment stage from the item row, when the drug came from one. */
  fulfillmentStage?: string | null;
}

/**
 * Every drug on the order, billing-snapshot entries first (in their stored
 * order), then any line item the snapshot has forgotten.
 *
 * Snapshot entries with no rxNumber are kept — legacy orders render them today
 * and dropping them would be a regression — but they can never be matched
 * against an item, so they are emitted as-is.
 */
export const deriveOrderDrugList = (
  order: DrugListOrderLike | null | undefined,
): OrderDrugListEntry[] => {
  if (!order) return [];

  const medications = Array.isArray(order.metadata?.billingData?.medications)
    ? order.metadata!.billingData!.medications!
    : [];
  const items = Array.isArray(order.items) ? order.items : [];

  const seenRx = new Set<string>();
  const list: OrderDrugListEntry[] = [];

  medications.forEach((med) => {
    const rx = rxKey(med?.rxNumber);
    if (rx) {
      if (seenRx.has(rx)) return;
      seenRx.add(rx);
    }
    list.push({
      ...med,
      rxNumber: rx,
      medicationName: med?.medicationName || 'Unknown Medication',
      fromLineItemOnly: false,
    });
  });

  items.forEach((item) => {
    const rx = rxKey(item?.rxNumber);
    // An item with no Rx has nothing to fetch a hardcopy or verification with —
    // it would render as a dead tab, so it is not synthesized into the list.
    if (!rx || seenRx.has(rx)) return;
    seenRx.add(rx);
    list.push({
      rxNumber: rx,
      medicationName: item?.medicationName || 'Unknown Medication',
      strength: item?.strength,
      quantity: item?.quantity,
      daysSupply: item?.daysSupply,
      din: item?.productDin,
      upc: item?.productUpc,
      drugId: item?.drugId,
      // packSize lives only on the billing snapshot — leaving it undefined keeps
      // the box-count "unknown" rather than inventing a pack size.
      fromLineItemOnly: true,
      fulfillmentStage: item?.fulfillmentStage ?? null,
    });
  });

  return list;
};
